import { moveItem } from "@/lib/pricing-draft";
import { optionsForType } from "@/lib/quote";
import { iconButtonClass, inputClass, numberInputClass } from "@/pages/admin/fieldClasses";
import type { PricingBody, PricingPreset } from "@/types/pricing";

/**
 * 프리셋(예시 업종) 편집 표. 유형 select + 옵션 다중 선택이 필요해서 `ItemTable` 로는 안 돼요.
 * 저장은 단가 탭의 draft·발행 흐름을 그대로 타요 — 프리셋도 `PricingBody` 의 일부예요.
 */
export function PresetTable({
  body,
  onChange,
}: {
  body: PricingBody;
  onChange: (next: PricingPreset[]) => void;
}) {
  const presets = body.presets;
  const update = (index: number, patch: Partial<PricingPreset>) =>
    onChange(presets.map((preset, i) => (i === index ? { ...preset, ...patch } : preset)));

  /** 유형을 바꾸면 그 유형에 안 붙는 옵션은 즉시 빼요 — 저장할 때 검증 에러로 튕기는 대신 편집 중에 정리해요. */
  const changeType = (index: number, type: string) => {
    const usable = new Set(optionsForType(body.options, type).map((o) => o.key));
    update(index, { type, options: presets[index].options.filter((key) => usable.has(key)) });
  };

  const toggle = (index: number, key: string) => {
    const current = presets[index].options;
    update(index, {
      options: current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    });
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="wds-headline-2 text-label-strong">예시 프리셋</h2>
        <p className="wds-caption-1 text-label-assistive">
          견적 화면의 접힌 줄("예시로 채워보세요")을 펼치면 모든 프리셋이 프로젝트 유형별로 묶여 한 번에
          보여요. 같은 유형 안에서 이 순서대로 나오고, 프리셋이 하나도 없는 유형은 묶음 자체가 빠져요.
          문의자가 프리셋을 고르면 프로젝트 유형까지 그 프리셋 값으로 바뀌어요.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg bg-background-elevated shadow-normal">
        <table className="w-full min-w-[60rem] wds-body-2">
          <thead>
            <tr className="border-line-alternative border-b text-label-assistive">
              <th className="px-3 py-3 text-left font-medium">키</th>
              <th className="px-3 py-3 text-left font-medium">라벨</th>
              <th className="px-3 py-3 text-left font-medium">설명</th>
              <th className="px-3 py-3 text-left font-medium">유형</th>
              <th className="px-3 py-3 text-right font-medium">페이지</th>
              <th className="px-3 py-3 text-left font-medium">기능·옵션</th>
              <th className="px-3 py-3 text-left font-medium">월 지원</th>
              <th className="px-3 py-3 text-right font-medium">순서·삭제</th>
            </tr>
          </thead>
          <tbody>
            {presets.map((preset, index) => (
              <tr key={index} className="border-line-alternative border-b align-top">
                <td className="px-3 py-2">
                  <input
                    aria-label={`프리셋 ${index + 1}번 키`}
                    value={preset.key}
                    onChange={(e) => update(index, { key: e.target.value })}
                    className={`${inputClass} w-32`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    aria-label={`프리셋 ${index + 1}번 라벨`}
                    value={preset.label}
                    onChange={(e) => update(index, { label: e.target.value })}
                    className={`${inputClass} w-36`}
                  />
                </td>
                <td className="px-3 py-2">
                  <textarea
                    aria-label={`프리셋 ${index + 1}번 설명`}
                    value={preset.desc}
                    onChange={(e) => update(index, { desc: e.target.value })}
                    rows={2}
                    className={`${inputClass} min-w-[16rem]`}
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    aria-label={`프리셋 ${index + 1}번 유형`}
                    value={preset.type}
                    onChange={(e) => changeType(index, e.target.value)}
                    className={`${inputClass} w-32`}
                  >
                    {body.project_types.map((type) => (
                      <option key={type.key} value={type.key}>
                        {type.label || type.key}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input
                    aria-label={`프리셋 ${index + 1}번 페이지 수`}
                    type="number"
                    min={0}
                    max={body.max_pages}
                    step={1}
                    value={preset.pages}
                    onChange={(e) => update(index, { pages: Number.parseInt(e.target.value, 10) || 0 })}
                    className={`${numberInputClass} w-24`}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex min-w-[20rem] flex-wrap gap-2">
                    {optionsForType(body.options, preset.type).map((option) => (
                      <label
                        key={option.key}
                        className="wds-caption-1 flex items-center gap-1 text-label-neutral"
                      >
                        <input
                          type="checkbox"
                          checked={preset.options.includes(option.key)}
                          onChange={() => toggle(index, option.key)}
                          className="h-4 w-4 accent-primary"
                        />
                        {option.label || option.key}
                      </label>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <select
                    aria-label={`프리셋 ${index + 1}번 월 지원`}
                    value={preset.support}
                    onChange={(e) => update(index, { support: e.target.value })}
                    className={`${inputClass} w-28`}
                  >
                    {body.support_tiers.map((tier) => (
                      <option key={tier.key} value={tier.key}>
                        {tier.label || tier.key}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      aria-label="위로"
                      disabled={index === 0}
                      onClick={() => onChange(moveItem(presets, index, -1))}
                      className={iconButtonClass}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label="아래로"
                      disabled={index === presets.length - 1}
                      onClick={() => onChange(moveItem(presets, index, 1))}
                      className={iconButtonClass}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label="삭제"
                      onClick={() => onChange(presets.filter((_, i) => i !== index))}
                      className={`${iconButtonClass} text-status-negative`}
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="px-3 py-3">
          <button
            type="button"
            onClick={() =>
              onChange([
                ...presets,
                {
                  key: "",
                  label: "",
                  desc: "",
                  type: body.project_types[0]?.key ?? "",
                  pages: 1,
                  options: [],
                  support: body.support_tiers[0]?.key ?? "",
                },
              ])
            }
            className="h-9 rounded-md bg-background-alternative px-4 wds-caption-1 font-semibold text-label-neutral"
          >
            프리셋 추가
          </button>
        </div>
      </div>
    </section>
  );
}

export default PresetTable;
